import React, { useState, useEffect, useMemo } from 'react';
import {
  ChevronDown,
  Download,
  Filter,
  MoreVertical,
  SlidersHorizontal,
  Store,
  UserRound,
} from 'lucide-react';
import { generateClient } from 'aws-amplify/api';
import * as mutations from '../graphql/mutations';
import useOperationalStore from '../platform/store/useOperationalStore';
import useShopSettings from '../platform/settings/useShopSettings';
import { computePriorityScore, urgencyTier } from '../platform/engine/priorityScore';
import { moneyState, moneyTotals, activeEstimate, estimateApprovalUrl } from '../platform/engine/estimateMoney';
import { getCurrentRole } from '../shared/permissions';
import { nowSeconds } from '../platform/events/operationEvents';
import FullscreenBtn from '../components/FullscreenBtn';
import './PriorityView.css';

const client = generateClient();

function stripTypename(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripTypename);
  const { __typename, ...rest } = obj;
  return Object.fromEntries(Object.entries(rest).map(([k, v]) => [k, stripTypename(v)]));
}

const PAGE_SIZE = 10;

const TIERS = [
  { rank: 0, id: 'critical', label: 'Critical', rule: '>$2,000 & >48hrs', color: '#DC2626' },
  { rank: 1, id: 'high', label: 'High', rule: '>$1,000 & >24hrs', color: '#EA580C' },
  { rank: 2, id: 'medium', label: 'Medium', rule: '>$500 & >12hrs', color: '#F59E0B' },
  { rank: 3, id: 'low', label: 'Low', rule: '<$500 & >6hrs', color: '#2563EB' },
];

const MONEY_META = {
  none:     { label: 'No estimate',       color: '#94A3B8' },
  draft:    { label: 'Draft estimate',    color: '#64748B' },
  sent:     { label: 'Awaiting approval', color: '#EA580C' },
  approved: { label: 'Approved',          color: '#16A34A' },
  declined: { label: 'Declined',          color: '#DC2626' },
  invoiced: { label: 'Invoiced',          color: '#7C3AED' },
  paid:     { label: 'Paid',              color: '#15803D' },
};

const STAGE_META = {
  yard: { label: 'Yard', color: '#475569', bg: '#F1F5F9' },
  triage: { label: 'Parts Auth', color: '#EA580C', bg: '#FFEDD5' },
  waiting: { label: 'In Progress', color: '#2563EB', bg: '#DBEAFE' },
  approval: { label: 'Parts Auth', color: '#EA580C', bg: '#FFEDD5' },
  'parts-hold': { label: 'Parts Hold', color: '#7C3AED', bg: '#EDE9FE' },
  wip: { label: 'In Progress', color: '#2563EB', bg: '#DBEAFE' },
  'road-test': { label: 'Road Test', color: '#16A34A', bg: '#DCFCE7' },
  qc: { label: 'Road Test', color: '#16A34A', bg: '#DCFCE7' },
};

const NEXT_ACTION = {
  yard: ['Assign technician', 'Needs service manager'],
  triage: ['Authorize parts', 'Estimate waiting'],
  waiting: ['Finish diagnosis', 'Customer needs update'],
  approval: ['Call for approval', 'Estimate sent'],
  'parts-hold': ['Confirm parts ETA', 'Vendor follow-up'],
  wip: ['Continue repair', 'Technician assigned'],
  'road-test': ['Complete road test', 'QC pending'],
  qc: ['Ready for closeout', 'Final inspection'],
};

const QUICK_FILTERS = [
  { id: 'critical', label: 'Show Critical', tone: 'red', test: r => r.rank === 0 },
  { id: 'customer', label: 'Waiting on Customer', tone: 'orange', test: r => r.money.stage === 'sent' },
  { id: 'parts', label: 'Waiting on Parts', tone: 'purple', test: r => r.truck.status === 'parts-hold' },
  { id: 'down', label: 'Down Trucks', tone: 'red', test: r => !!r.truck.isHardDown },
  { id: 'promised', label: 'Past Promised Date', tone: 'red', test: (r, now) => {
    const p = r.truck.promisedTime;
    if (!p) return false;
    return (p < 10_000_000_000 ? p * 1000 : p) < now;
  } },
];

const AGING_BUCKETS = [
  { label: '> 72 Hours', min: 72, max: Infinity, color: '#DC2626' },
  { label: '48 - 72 Hours', min: 48, max: 72, color: '#EA580C' },
  { label: '24 - 48 Hours', min: 24, max: 48, color: '#F59E0B' },
  { label: '12 - 24 Hours', min: 12, max: 24, color: '#2563EB' },
  { label: '6 - 12 Hours', min: 6, max: 12, color: '#94A3B8' },
  { label: '< 6 Hours', min: 0, max: 6, color: '#CBD5E1' },
];

function fmtCurrency(n) {
  if (!n) return '$0.00';
  return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function stageAgeMs(truck, now) {
  const raw = truck.lastStatusChange || (truck.startTime ? truck.startTime * 1000 : now);
  const timestamp = raw < 10_000_000_000 ? raw * 1000 : raw;
  return Math.max(0, now - timestamp);
}

function ageDisplay(ms) {
  const hours = Math.floor(ms / 3_600_000);
  if (hours >= 24) return `${Math.floor(hours / 24)}d ${hours % 24}h`;
  return `${Math.max(1, hours)}h`;
}

function ageRule(ms) {
  const hours = ms / 3_600_000;
  if (hours > 48) return { text: '>48h', color: '#DC2626' };
  if (hours > 24) return { text: '>24h', color: '#EA580C' };
  if (hours > 12) return { text: '>12h', color: '#F59E0B' };
  return { text: '>6h', color: '#2563EB' };
}

function scoreTone(score) {
  if (score >= 90) return 'critical';
  if (score >= 70) return 'high';
  if (score >= 50) return 'medium';
  return 'low';
}

function moneySub(money, now) {
  const meta = MONEY_META[money.stage] || MONEY_META.none;
  if (money.stage === 'none') return { text: money.total ? 'Internal est.' : 'No estimate', color: meta.color };
  if (money.stage === 'sent' && money.stallMs != null) {
    return { text: `${meta.label} · ${ageDisplay(money.stallMs)}`, color: meta.color };
  }
  if (money.stage === 'approved' && money.approvedBy) {
    return { text: `Approved — ${money.approvedBy}`, color: meta.color };
  }
  if (money.stage === 'invoiced' && money.overdueMs > 0) {
    return { text: `Overdue · ${ageDisplay(money.overdueMs)}`, color: '#DC2626' };
  }
  if (money.stage === 'invoiced' && money.stallMs != null) {
    return { text: `Invoiced · ${ageDisplay(money.stallMs)}`, color: meta.color };
  }
  return { text: meta.label, color: meta.color };
}

function PrioritySidebar({ rows, now, activeFilter, onFilter }) {
  const totals = useMemo(() => moneyTotals(rows.map(r => r.truck), now), [rows, now]);

  const moneyRows = [
    { label: 'Awaiting Approval', value: totals.sent, tone: 'orange' },
    { label: 'Approved (In Shop)', value: totals.approved, tone: 'green' },
    { label: 'Invoiced', value: totals.invoiced - totals.overdue, tone: 'purple' },
    { label: 'Overdue', value: totals.overdue, tone: 'red' },
  ];

  const agingCounts = AGING_BUCKETS.map(bucket => ({
    ...bucket,
    count: rows.filter(r => {
      const hours = r.age / 3_600_000;
      return hours >= bucket.min && hours < bucket.max;
    }).length,
  }));
  const maxAging = Math.max(1, ...agingCounts.map(b => b.count));

  return (
    <aside className="pv-sidebar" aria-label="Priority details">
      <section className="pv-side-card">
        <h2>Money Loop</h2>
        <div className="pv-money-list">
          {moneyRows.map(row => (
            <div className="pv-money-row" key={row.label}>
              <span>{row.label}</span>
              <span className={`pv-money-amount pv-money-amount--${row.tone}`}>{fmtCurrency(row.value)}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="pv-side-card">
        <div className="pv-side-title-row">
          <h2>Priority Settings</h2>
          <button>Edit</button>
        </div>
        <p>Priority is calculated based on:</p>
        <ul className="pv-settings-list">
          <li>Est. Total</li>
          <li>Days in Current Stage</li>
          <li>Customer Type</li>
          <li>Promised Date</li>
          <li>Down Status</li>
        </ul>
        <button className="pv-link-button">Learn more</button>
      </section>

      <section className="pv-side-card">
        <h2>Quick Filters</h2>
        <div className="pv-quick-list">
          {QUICK_FILTERS.map(filter => {
            const count = rows.filter(r => filter.test(r, now)).length;
            return (
              <button
                className={`pv-quick-row${activeFilter === filter.id ? ' is-active' : ''}`}
                key={filter.id}
                onClick={() => onFilter(activeFilter === filter.id ? null : filter.id)}
              >
                <span>{filter.label}</span>
                <span className={`pv-count-badge pv-count-badge--${filter.tone}`}>{count}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="pv-side-card">
        <h2>Aging (All Trucks)</h2>
        <div className="pv-aging-list">
          {agingCounts.map(item => (
            <div className="pv-aging-row" key={item.label}>
              <span className="pv-aging-label">{item.label}</span>
              <span className="pv-aging-track">
                <span className="pv-aging-bar" style={{ width: `${Math.round((item.count / maxAging) * 100)}%`, backgroundColor: item.color }} />
              </span>
              <span className="pv-aging-count">{item.count}</span>
            </div>
          ))}
        </div>
      </section>
    </aside>
  );
}

function FilterButton({ icon, label, chevron = true }) {
  return (
    <button className="pv-filter-button">
      {icon}
      <span>{label}</span>
      {chevron && <ChevronDown size={15} />}
    </button>
  );
}

export default function PriorityView() {
  const trucks = useOperationalStore(s => s.trucks);
  const setSelected = useOperationalStore(s => s.setSelectedTruckId);
  const business = useShopSettings(s => s.settings.business);
  const [sortBy, setSortBy] = useState('priority');
  const [page, setPage] = useState(1);
  const [now, setNow] = useState(Date.now());
  const [quickFilter, setQuickFilter] = useState(null);
  const [nudgedId, setNudgedId] = useState(null);

  async function sendApprovalNudge(e, truck, money) {
    e.stopPropagation();
    const est = activeEstimate(truck);
    if (!est || est.status !== 'sent' || !est.token) return;

    const url = estimateApprovalUrl(est.token);
    try { await navigator.clipboard?.writeText(url); } catch {}

    const shopName = business?.shopName || business?.companyName || 'REKON';
    const message = `${shopName}: estimate for RO ${truck.repairOrder} (${fmtCurrency(money.total)}) is awaiting your approval: ${url}`;
    const phone = (truck.phone || '').replace(/[^\d+]/g, '');
    let via = 'link copied';
    if (phone) {
      via = 'SMS';
      window.location.href = `sms:${encodeURIComponent(phone)}?&body=${encodeURIComponent(message)}`;
    } else if (truck.email) {
      via = 'email';
      window.location.href = `mailto:${encodeURIComponent(truck.email)}?subject=${encodeURIComponent(`Estimate awaiting approval - RO ${truck.repairOrder}`)}&body=${encodeURIComponent(message)}`;
    }

    setNudgedId(truck.id);
    setTimeout(() => setNudgedId(current => (current === truck.id ? null : current)), 4000);

    const activity = {
      id: `act-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      type: 'note',
      text: `Approval reminder sent - ${fmtCurrency(money.total)} - via ${via}`,
      author: getCurrentRole(),
      timestamp: nowSeconds(),
      customerFacing: true,
      stage: truck.status || null,
    };
    try {
      await client.graphql({
        query: mutations.updateTruck,
        variables: {
          input: {
            id: truck.id,
            activities: [activity, ...stripTypename(truck.activities || [])],
            lastNote: nowSeconds(),
          },
        },
      });
    } catch (err) {
      console.error('Failed to stamp approval nudge', err);
    }
  }

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const rows = useMemo(() => {
    return trucks
      .filter(t => !['completed', 'voided'].includes(t.status))
      .map(truck => {
        const rank = urgencyTier(truck);
        const score = computePriorityScore(truck);
        const age = stageAgeMs(truck, now);
        const money = moneyState(truck, now);
        return { truck, rank, score, age, money, estTotal: money.total };
      });
  }, [trucks, now]);

  const tierCounts = useMemo(() => {
    const counts = [0, 0, 0, 0];
    rows.forEach(r => { counts[Math.min(r.rank, 3)] += 1; });
    return counts;
  }, [rows]);

  const filtered = useMemo(() => {
    if (!quickFilter) return rows;
    const filter = QUICK_FILTERS.find(f => f.id === quickFilter);
    return filter ? rows.filter(r => filter.test(r, now)) : rows;
  }, [rows, quickFilter, now]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    if (sortBy === 'stage-age') return copy.sort((a, b) => b.age - a.age);
    if (sortBy === 'est-total') return copy.sort((a, b) => b.estTotal - a.estTotal);
    return copy.sort((a, b) => a.rank - b.rank || b.score - a.score);
  }, [filtered, sortBy]);

  const pageRows = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const start = sorted.length ? (page - 1) * PAGE_SIZE + 1 : 0;
  const end = Math.min(page * PAGE_SIZE, sorted.length);

  return (
    <div className="pv-root">
      <div className="pv-content">
        <div className="pv-main">
          <div className="pv-toolbar">
            <div className="pv-toolbar-left">
              <FilterButton icon={<Store size={16} />} label="All Shops" />
              <FilterButton icon={<UserRound size={16} />} label="All Service Managers" />
              <FilterButton label="All Statuses" />
              <FilterButton icon={<Filter size={16} />} label="Filters" chevron={false} />
              <FilterButton label="Group: None" />
            </div>
            <button className="pv-export-button">
              <Download size={16} />
              <span>Export</span>
            </button>
            <FullscreenBtn />
          </div>

          <section className="pv-summary-card">
            <div className="pv-summary-metrics">
              {TIERS.map(tier => (
                <button className="pv-summary-segment" key={tier.id} style={{ '--tier-color': tier.color }}>
                  <span className="pv-summary-label">{tier.label}</span>
                  <span className="pv-summary-number">{tierCounts[tier.rank]}</span>
                  <span className="pv-summary-rule">{tier.rule}</span>
                </button>
              ))}
              <div className="pv-summary-segment pv-summary-total">
                <span className="pv-summary-label">Total</span>
                <span className="pv-summary-number">{rows.length}</span>
                <span className="pv-summary-rule">Trucks</span>
              </div>
            </div>
            <div className="pv-sort-area">
              <label>Sort by</label>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="priority">Priority Score</option>
                <option value="stage-age">Days in Stage</option>
                <option value="est-total">Est. Total</option>
              </select>
              <button aria-label="Priority settings">
                <SlidersHorizontal size={16} />
              </button>
            </div>
          </section>

          <section className="pv-table-card">
            <div className="pv-table-scroll">
              <table className="pv-table">
                <thead>
                  <tr>
                    <th>Priority</th>
                    <th>RO #</th>
                    <th>Customer</th>
                    <th>Asset</th>
                    <th>Current Stage</th>
                    <th>Days in Stage</th>
                    <th>Est. Total</th>
                    <th>Priority Score</th>
                    <th>Next Action</th>
                    <th>Advisor</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map(({ truck, rank, score, age, money }) => {
                    const tier = TIERS[rank] || TIERS[3];
                    const stage = STAGE_META[truck.status] || STAGE_META.yard;
                    const rule = ageRule(age);
                    const vehicle = [truck.year, truck.make, truck.model].filter(Boolean).join(' ') || '—';
                    const next = NEXT_ACTION[truck.status] || ['Review repair order', ''];
                    const advisor = truck.serviceAdvisor || truck.advisor || '—';
                    const sub = moneySub(money, now);

                    return (
                      <tr key={truck.id} onClick={() => setSelected(truck.id)}>
                        <td>
                          <span className={`pv-priority-badge pv-priority-badge--${tier.id}`}>{tier.label}</span>
                        </td>
                        <td><button className="pv-ro-link">{truck.repairOrder || '—'}</button></td>
                        <td>
                          <div className="pv-cell-main">{truck.companyName || '—'}</div>
                          <div className="pv-cell-sub">{truck.phone || truck.contactPerson || ''}</div>
                        </td>
                        <td>
                          <div className="pv-cell-main">{vehicle}</div>
                          <div className="pv-cell-sub">{truck.truckNumber || truck.vin || ''}</div>
                        </td>
                        <td>
                          <span className="pv-stage-badge" style={{ backgroundColor: stage.bg, color: stage.color }}>{stage.label}</span>
                        </td>
                        <td>
                          <div className="pv-age-main">{ageDisplay(age)}</div>
                          <div className="pv-age-rule" style={{ color: rule.color }}>{rule.text}</div>
                        </td>
                        <td className="pv-money">
                          <div className="pv-money-main">{fmtCurrency(money.total)}</div>
                          {money.stage === 'sent' ? (
                            <button
                              className="pv-money-sub pv-money-nudge"
                              style={{ color: sub.color }}
                              title="Re-send approval link to customer"
                              onClick={e => sendApprovalNudge(e, truck, money)}
                            >
                              {nudgedId === truck.id ? 'Reminder sent ✓' : sub.text}
                            </button>
                          ) : (
                            <div className="pv-money-sub" style={{ color: sub.color }}>{sub.text}</div>
                          )}
                        </td>
                        <td>
                          <span className={`pv-score pv-score--${scoreTone(score)}`}>{score}</span>
                        </td>
                        <td>
                          <div className="pv-cell-main">{next[0]}</div>
                          {next[1] && <div className="pv-cell-sub">{next[1]}</div>}
                        </td>
                        <td className="pv-advisor">{advisor}</td>
                        <td>
                          <button className="pv-action-button" onClick={e => e.stopPropagation()} aria-label="Row actions">
                            <MoreVertical size={17} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="pv-table-footer">
              <span>Showing {start} to {end} of {sorted.length} trucks</span>
              <div className="pv-pages">
                <button disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Prev</button>
                {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => i + 1).map(p => (
                  <button key={p} className={page === p ? 'is-active' : ''} onClick={() => setPage(p)}>{p}</button>
                ))}
                <button disabled={page === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next</button>
              </div>
              <select defaultValue="10">
                <option value="10">10 / page</option>
                <option value="25">25 / page</option>
              </select>
            </div>
          </section>
        </div>
        <PrioritySidebar
          rows={rows}
          now={now}
          activeFilter={quickFilter}
          onFilter={id => { setQuickFilter(id); setPage(1); }}
        />
      </div>
    </div>
  );
}
